import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { auth } from '../firebase';
import {
  OfficeEntitlement,
  EntitlementResolutionState,
  DEFAULT_ENTITLEMENT,
  FAIL_CLOSED_ENTITLEMENT,
} from '../types';

interface EntitlementContextValue {
  entitlement: OfficeEntitlement;
  resolutionState: EntitlementResolutionState;
  error: string | null;
  refresh: () => Promise<void>;
}

const EntitlementContext = createContext<EntitlementContextValue>({
  entitlement: DEFAULT_ENTITLEMENT,
  resolutionState: 'loading',
  error: null,
  refresh: () => Promise.resolve(),
});

export function EntitlementProvider({ children }: { children: React.ReactNode }) {
  const [entitlement, setEntitlement] = useState<OfficeEntitlement>(DEFAULT_ENTITLEMENT);
  const [resolutionState, setResolutionState] = useState<EntitlementResolutionState>('loading');
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const resolveRef = useRef<(() => void) | null>(null);

  const refresh = useCallback(() => {
    return new Promise<void>((resolve) => {
      resolveRef.current = resolve;
      setRefreshKey(k => k + 1);
    });
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchEntitlement = async (token: string) => {
      try {
        const res = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-office-entitlement`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (cancelled) return;

        // HTTP-level failure → fail-closed
        if (!res.ok) {
          let errorMsg = `Entitlement service error (${res.status})`;
          try {
            const errBody = await res.json();
            if (errBody?.error) errorMsg = errBody.error;
          } catch { /* response body not JSON */ }
          setError(errorMsg);
          setEntitlement(FAIL_CLOSED_ENTITLEMENT);
          setResolutionState('error');
          resolveRef.current?.();
          return;
        }

        const data = await res.json();
        if (cancelled) return;

        // Application-level failure (edge function returned success: false) → fail-closed
        if (!data.success || !data.entitlement) {
          setError(data.error || 'Entitlement service returned an error');
          setEntitlement(FAIL_CLOSED_ENTITLEMENT);
          setResolutionState('error');
          resolveRef.current?.();
          return;
        }

        // Successfully resolved — covers both "has office" and "legitimately no office"
        setEntitlement(data.entitlement as OfficeEntitlement);
        setError(null);
        setResolutionState('resolved');
        resolveRef.current?.();
      } catch (err) {
        if (cancelled) return;
        // Network failure / timeout / unresponsive → fail-closed
        setError(err instanceof Error ? err.message : 'Network error contacting entitlement service');
        setEntitlement(FAIL_CLOSED_ENTITLEMENT);
        setResolutionState('error');
        resolveRef.current?.();
      }
    };

    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (cancelled) return;
      if (user) {
        setResolutionState('loading');
        user.getIdToken().then(fetchEntitlement);
      } else {
        // No authenticated user — this is NOT an error state.
        // Use DEFAULT_ENTITLEMENT (legacy compatibility for pre-auth state).
        setEntitlement(DEFAULT_ENTITLEMENT);
        setResolutionState('resolved');
        setError(null);
        resolveRef.current?.();
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [refreshKey]);

  return (
    <EntitlementContext.Provider value={{ entitlement, resolutionState, error, refresh }}>
      {children}
    </EntitlementContext.Provider>
  );
}

export function useEntitlement(): EntitlementContextValue {
  return useContext(EntitlementContext);
}